import { useState, useCallback, useEffect } from "react";
import cadviewer from "cadviewer";
import { getSelectedSpaces } from "../utils/spaceManagement";
import { SearchMode } from "../types/drawerTypes";
import EventSystem from "../../../../../events/EventSystem";

export const useSpaceSelection = (
  multiSelectArray: string[],
  searchMode: SearchMode,
  setSearchMode: (mode: SearchMode) => void,
  setMultiSelectArray: (spaces: string[]) => void
) => {
  const [selectedSpacesChips, setSelectedSpacesChips] = useState<string[]>([]);

  const handleRemoveSpaceChip = useCallback((spaceToRemove: string) => {
    // Turn off highlight in CADViewer for the deselected space
    cadviewer.cvjs_removeMultiSelectItem(spaceToRemove);
    
    setSelectedSpacesChips((prev) =>
      prev.filter((space) => space !== spaceToRemove)
    );
  }, []);

  const handleClearAllSpaceChips = useCallback(() => {
    setSelectedSpacesChips([]);
    // clear space on canvas
    cadviewer.cvjs_clearSpaceLayer();
    cadviewer.cvjs_clearMultiSelectArrays(); // clear CADViewer multiselect
    setMultiSelectArray([]); // clear context
  }, [setMultiSelectArray]);

  useEffect(() => {
    const handleAutoAddSpaces = (data: any) => {
      const spaces = data as string[];
      const uniqueSpaces = getSelectedSpaces(spaces);
      setSelectedSpacesChips(uniqueSpaces);

      // Auto-switch to "current" mode if in "global" mode
      if (uniqueSpaces.length > 0 && searchMode === "global") {
        setSearchMode("current");
      }
    };

    EventSystem.subscribe("cadviewer.auto_add_spaces", handleAutoAddSpaces);
    EventSystem.subscribe("cadviewer.cvjs_onLoadEnd", handleClearAllSpaceChips);
    
    return () => {
      EventSystem.unsubscribe("cadviewer.auto_add_spaces", handleAutoAddSpaces);
      EventSystem.unsubscribe("cadviewer.cvjs_onLoadEnd", handleClearAllSpaceChips);
    };
  }, [searchMode, setSearchMode, handleClearAllSpaceChips]);

  const handleAddSelectedSpaces = useCallback(() => {
    const uniqueSpaces = getSelectedSpaces(multiSelectArray);

    if (uniqueSpaces.length === 0) {
      console.log("No spaces selected");
      return;
    }

    // REPLACE chips with currently selected spaces (not accumulate)
    setSelectedSpacesChips(uniqueSpaces);

    // Auto-switch to "current" mode if in "global" mode
    if (searchMode === "global") {
      setSearchMode("current");
    }
  }, [multiSelectArray, searchMode, setSearchMode]);

  return {
    selectedSpacesChips,
    setSelectedSpacesChips,
    handleAddSelectedSpaces,
    handleRemoveSpaceChip,
    handleClearAllSpaceChips,
  };
};
